/**
 * Flight log shared schemas + options.
 *
 * Browser-safe: imports only zod. Consumed by both the API
 * (flightLog.service.ts) and the app (via the @@api alias) so the drawer form
 * and the server validate the exact same shapes.
 *
 * Conventions:
 * - Clock times ("offBlock", "takeOff", …) are "HH:mm" strings in UTC.
 * - Durations ("blockTime", hours cells, …) are "h:mm" strings (no cap on
 *   hours — airframe totals run into the thousands).
 * - Derived values (block/flight time, new totals, fuel used) are computed
 *   client-side and stored as entered; the server validates shape only.
 */
import { z } from 'zod'

export const natureOfFlightOptions = [
  'Part91',
  'Part135',
  'Positioning',
  'Training',
  'Maintenance',
  'Other',
] as const

export const natureOfFlightLabels: Record<(typeof natureOfFlightOptions)[number], string> = {
  Part91: 'Part 91',
  Part135: 'Part 135',
  Positioning: 'Positioning',
  Training: 'Training',
  Maintenance: 'Maintenance',
  Other: 'Other',
}

export const crewDutyOptions = ['PIC', 'SIC', 'FA', 'FE', 'OTHER'] as const

export const crewDutyLabels: Record<(typeof crewDutyOptions)[number], string> = {
  PIC: 'Pilot in Command',
  SIC: 'Second in Command',
  FA: 'Flight Attendant',
  FE: 'Flight Engineer',
  OTHER: 'Other (3rd pilot)',
}

// Duties that fly the aircraft and therefore carry a PF/PM toggle
export const pfPmDuties = ['PIC', 'SIC', 'OTHER'] as const

export const pfPmOptions = ['PF', 'PM'] as const

// Fixed crew positions requested by flight ops, in on-screen order. The
// persisted shape stays `routeTimes.crew[]` (duty + pfPm); a position is a
// view over it: PF ⇄ PIC/PF, PM ⇄ SIC/PM, AUGMENTED ⇄ OTHER, FA1/FA2 ⇄ the
// first two FA entries, FE ⇄ FE. PIC is by definition the Pilot Flying.
export const crewPositions = ['PF', 'PM', 'AUGMENTED', 'FA1', 'FA2', 'FE'] as const
export type CrewPosition = (typeof crewPositions)[number]

export const crewPositionLabels: Record<CrewPosition, string> = {
  PF: 'Pilot Flying (PF)',
  PM: 'Pilot Monitoring (PM)',
  AUGMENTED: 'Augmented Crew',
  FA1: 'F/A 1',
  FA2: 'F/A 2',
  FE: 'Engineer',
}

export type CrewEntryLike = {
  crewId: string | null
  name: string
  duty: (typeof crewDutyOptions)[number]
  pfPm: (typeof pfPmOptions)[number] | null
}

export function crewEntryForPosition<T extends { crewId: string | null; name: string }>(
  position: CrewPosition,
  member: T
): CrewEntryLike {
  const base = { crewId: member.crewId, name: member.name }
  switch (position) {
    case 'PF':
      return { ...base, duty: 'PIC', pfPm: 'PF' }
    case 'PM':
      return { ...base, duty: 'SIC', pfPm: 'PM' }
    case 'AUGMENTED':
      return { ...base, duty: 'OTHER', pfPm: null }
    case 'FA1':
    case 'FA2':
      return { ...base, duty: 'FA', pfPm: null }
    case 'FE':
      return { ...base, duty: 'FE', pfPm: null }
  }
}

export type CrewSlots = Record<CrewPosition, CrewEntryLike | null>

export function crewSlotsFromEntries(crew: readonly CrewEntryLike[]): CrewSlots {
  const slots: CrewSlots = { PF: null, PM: null, AUGMENTED: null, FA1: null, FA2: null, FE: null }
  for (const entry of crew) {
    let position: CrewPosition | null = null
    switch (entry.duty) {
      case 'PIC':
        position = 'PF'
        break
      case 'SIC':
        position = 'PM'
        break
      case 'OTHER':
        position = 'AUGMENTED'
        break
      case 'FA':
        position = slots.FA1 ? 'FA2' : 'FA1'
        break
      case 'FE':
        position = 'FE'
        break
    }
    if (position && !slots[position]) slots[position] = entry
  }
  return slots
}

export function crewEntriesFromSlots(slots: CrewSlots): CrewEntryLike[] {
  return crewPositions.flatMap((position) => {
    const entry = slots[position]
    return entry ? [crewEntryForPosition(position, entry)] : []
  })
}

// Sort key matching crewPositions order
export function crewOrderRank(entry: { duty: (typeof crewDutyOptions)[number] }): number {
  switch (entry.duty) {
    case 'PIC':
      return 0
    case 'SIC':
      return 1
    case 'OTHER':
      return 2
    case 'FA':
      return 3
    case 'FE':
      return 4
    default:
      return 5
  }
}

export function sortCrewByPosition<T extends Parameters<typeof crewOrderRank>[0]>(
  crew: readonly T[]
): T[] {
  return crew
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) => crewOrderRank(a.entry) - crewOrderRank(b.entry) || a.index - b.index)
    .map(({ entry }) => entry)
}

export const approachTypeOptions = [
  'ILS',
  'RNP-LNAV',
  'RNP-LPV',
  'VOR',
  'NDB',
  'Visual',
  'Circling',
  'Other',
] as const
export const inspectionTargetOptions = ['Airframe', 'Engine'] as const

// "HH:mm" 24h clock (UTC)
const hhmmClock = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:mm (UTC)')
// "h:mm" duration — hours unbounded (running totals)
const hmmDuration = z.string().regex(/^\d{1,5}:[0-5]\d$/, 'Use h:mm')

export const flightLogCrewEntrySchema = z.object({
  crewId: z.string().nullable(), // null for manually added rows
  name: z.string().min(1),
  duty: z.enum(crewDutyOptions),
  pfPm: z.enum(pfPmOptions).nullable(), // pilots only; null for FA
})

export const routeTimesSchema = z.object({
  from: z.string().min(1), // ICAO, prefilled from leg
  to: z.string().min(1),
  paxCount: z.number().int().min(0),
  // Drafts may have no crew yet; submit enforces ≥1 (see flightLogSubmitSchema)
  crew: z.array(flightLogCrewEntrySchema),
  offBlock: hhmmClock.nullable(),
  onBlock: hhmmClock.nullable(),
  takeOff: hhmmClock.nullable(),
  landing: hhmmClock.nullable(),
  blockTime: hmmDuration.nullable(), // computed: off block → on block
  flightTime: hmmDuration.nullable(), // computed: take-off → landing
  distanceNm: z.number().min(0).nullable(),
})

export const approachSchema = z.object({
  type: z.enum(approachTypeOptions),
  runway: z.string(),
})

// One take-off and one landing per leg — each is either in daylight or dark
export const dayNightOptions = ['Day', 'Night'] as const

export const conditionsSchema = z.object({
  // Durations flown in daylight / in dark; auto-defaulted from flight time +
  // the take-off/landing phases but editable
  dayTime: hmmDuration,
  nightTime: hmmDuration,
  takeOff: z.enum(dayNightOptions).nullable(),
  landing: z.enum(dayNightOptions).nullable(),
  instrumentTime: hmmDuration.nullable(),
  approaches: z.array(approachSchema),
})

export const hoursCellSchema = z.object({
  broughtFwd: hmmDuration,
  thisLeg: hmmDuration,
  newTotal: hmmDuration, // computed: broughtFwd + thisLeg
})

export const cyclesCellSchema = z.object({
  broughtFwd: z.number().int().min(0),
  thisLeg: z.number().int().min(0),
  newTotal: z.number().int().min(0), // computed: broughtFwd + thisLeg
})

export const hoursSchema = z.object({
  engineCount: z.number().int().min(1).max(4),
  airframe: hoursCellSchema,
  engines: z.array(hoursCellSchema), // length === engineCount
  apu: hoursCellSchema,
  cycles: z.object({
    engines: z.array(cyclesCellSchema), // length === engineCount
    apu: cyclesCellSchema,
    landings: cyclesCellSchema,
  }),
})

export const fuelSchema = z.object({
  plannedUpliftL: z.number().min(0).nullable(),
  actualUpliftL: z.number().min(0).nullable(),
  fuelDocNo: z.string().nullable(),
  departTotalLbs: z.number().min(0).nullable(),
  arrivalTotalLbs: z.number().min(0).nullable(),
  usedLbs: z.number().nullable(), // computed: depart − arrival
})

export const deiceSchema = z.object({
  performed: z.boolean(),
  fluidType: z.string().nullable(), // e.g. Type I / II / IV
  mixRatio: z.string().nullable(),
  startTime: hhmmClock.nullable(),
  stopTime: hhmmClock.nullable(),
})

export const oilEngineSchema = z.object({
  upliftQts: z.number().min(0).nullable(),
  remainingQts: z.number().min(0).nullable(), // 10 min after shutdown
})

export const oilSchema = z.object({
  engines: z.array(oilEngineSchema),
  apuUpliftQts: z.number().min(0).nullable(),
  hydUpliftQts: z.number().min(0).nullable(),
  psiDif: z.number().nullable(),
})

export const ectmEngineSchema = z.object({
  n1Pct: z.number().nullable(),
  n2Pct: z.number().nullable(),
  itt: z.number().nullable(),
  fuelFlow: z.number().nullable(),
  oilPress: z.number().nullable(),
  oilTemp: z.number().nullable(),
  fuelTemp: z.number().nullable(),
  bleedPress: z.number().nullable(),
})

export const ectmSchema = z.object({
  // Flight-level snapshot conditions for the readings
  weightKg: z.number().min(0).nullable(),
  oatSatC: z.number().nullable(),
  iasKt: z.number().min(0).nullable(),
  tasKt: z.number().min(0).nullable(),
  mach: z.number().min(0).nullable(),
  flightLevel: z.number().min(0).nullable(), // e.g. 410
  engines: z.array(ectmEngineSchema),
})

// Minutes → "h:mm" (hours unbounded)
const minutesToHmm = (minutes: number) => {
  const total = Math.max(0, Math.round(minutes))
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`
}

// "h:mm" duration → minutes. Strict (matches hmmDuration); null on
// missing/invalid input. Used server-side to sum logged block/flight times.
export const hmmToMinutes = (value: string | null | undefined): number | null => {
  if (!value) return null
  const match = /^(\d{1,5}):([0-5]\d)$/.exec(value.trim())
  if (!match) return null
  return parseInt(match[1], 10) * 60 + parseInt(match[2], 10)
}

export const inspectionSchema = z.object({
  target: z.enum(inspectionTargetOptions),
  // "h:mm" running total at which the inspection falls due. Older logs and
  // app builds stored a plain hour count — normalised here so they keep
  // loading and saving.
  dueHours: z.preprocess(
    (value) => (typeof value === 'number' ? minutesToHmm(value * 60) : value),
    hmmDuration.nullable()
  ),
  dueDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
    .nullable(),
})

export const defectActionOptions = ['A', 'D'] as const

export const defectActionLabels: Record<(typeof defectActionOptions)[number], string> = {
  A: 'A — Action taken',
  D: 'D — Deferred (MEL)',
}

export const defectEntrySchema = z.object({
  description: z.string(), // may be empty in drafts; submit enforces min 1
  action: z.enum(defectActionOptions),
  melRef: z.string().nullable(), // optional; relevant when deferred
  actionNotes: z.string().nullable(), // action taken by engineer/pilot
})

export const defectsSchema = z.object({
  hasDefects: z.boolean(),
  entries: z.array(defectEntrySchema),
})

// Supporting paperwork photographed/attached to the log (fuel slips, load
// sheets, …). Files are uploaded to S3 from the drawer; only the pointers
// are stored here.
export const flightLogDocumentSchema = z.object({
  s3Key: z.string().min(1),
  fileName: z.string().min(1),
})

export const flightLogDocumentsSchema = z.array(flightLogDocumentSchema)

// Captain (PIC) sign-off — one signature before the flight, one after.
// Name/licence prefilled from the crew record; signature drawn on the pad.
export const signOffPhaseSchema = z.object({
  crewId: z.string().nullable(),
  name: z.string(),
  licenceNo: z.string(),
  // PNG data URL from the signature pad; capped to guard against payload abuse
  signaturePng: z.string().startsWith('data:image/png').max(200_000).nullable(),
  signedAt: z.string().datetime().nullable(), // ISO, UTC
})

export const signOffSchema = z.object({
  preFlight: signOffPhaseSchema.nullable(),
  postFlight: signOffPhaseSchema.nullable(),
})

// Draft: any subset may be present, and present sections may be incomplete.
export const flightLogDraftSchema = z.object({
  natureOfFlight: z.enum(natureOfFlightOptions).nullable(),
  routeTimes: routeTimesSchema.nullable(),
  conditions: conditionsSchema.nullable(),
  hours: hoursSchema.nullable(),
  fuel: fuelSchema.nullable(),
  deice: deiceSchema.nullable(),
  oil: oilSchema.nullable(),
  ectm: ectmSchema.nullable(),
  inspection: inspectionSchema.nullable(),
  defects: defectsSchema.nullable(),
  // Optional (not just nullable) so app builds predating the field still save
  documents: flightLogDocumentsSchema.nullish(),
  signOff: signOffSchema.nullable(),
})

// Required-field helpers for submit: null/empty resolves to a plain
// "Required" message instead of a zod type error.
const reqNum = z.number({ invalid_type_error: 'Required' })
const reqStr = z.string({ invalid_type_error: 'Required' }).min(1, 'Required')
const reqClock = z
  .string({ invalid_type_error: 'Required' })
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use HH:mm (UTC)')
const reqDuration = z
  .string({ invalid_type_error: 'Required' })
  .regex(/^\d{1,5}:[0-5]\d$/, 'Use h:mm')

// Submit: every field in a Required section is mandatory, with one
// exception — the fuel uplift trio (planned, actual, doc no) may be blank
// when the leg had no refuel. Conditional sections (deice, defects) enforce
// their fields only when toggled on; the Optional sections (oil, ECTM) stay
// fully optional.
export const flightLogSubmitSchema = z
  .object({
    natureOfFlight: z.enum(natureOfFlightOptions).nullable(),
    routeTimes: routeTimesSchema.extend({
      offBlock: reqClock,
      onBlock: reqClock,
      takeOff: reqClock,
      landing: reqClock,
      distanceNm: reqNum.min(0),
    }),
    conditions: conditionsSchema.extend({
      takeOff: z.enum(dayNightOptions, { invalid_type_error: 'Required' }),
      landing: z.enum(dayNightOptions, { invalid_type_error: 'Required' }),
      instrumentTime: reqDuration,
    }),
    hours: hoursSchema,
    fuel: fuelSchema
      .extend({
        departTotalLbs: reqNum.min(0),
        arrivalTotalLbs: reqNum.min(0),
      })
      // No refuel on this leg is valid (aircraft fuelled for several legs),
      // so the uplift fields may all be blank. A recorded uplift must carry
      // its fuel document number. The rule sits on its own branch of an
      // intersection: zod skips an object's superRefine whenever a sibling
      // field fails, which would hide this error until the totals are fixed.
      .and(
        fuelSchema.pick({ actualUpliftL: true, fuelDocNo: true }).superRefine((f, ctx) => {
          if ((f.actualUpliftL ?? 0) > 0 && !f.fuelDocNo?.trim())
            ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['fuelDocNo'], message: 'Required' })
        })
      ),
    deice: deiceSchema.superRefine((d, ctx) => {
      if (!d.performed) return
      if (!d.fluidType?.trim())
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['fluidType'], message: 'Required' })
      if (!d.mixRatio?.trim())
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['mixRatio'], message: 'Required' })
      if (!d.startTime)
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['startTime'], message: 'Required' })
      if (!d.stopTime)
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['stopTime'], message: 'Required' })
    }),
    oil: oilSchema,
    ectm: ectmSchema.nullable(),
    inspection: inspectionSchema.extend({
      dueHours: z.preprocess(
        (value) => (typeof value === 'number' ? minutesToHmm(value * 60) : value),
        reqDuration
      ),
      dueDate: z
        .string({ invalid_type_error: 'Required' })
        .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD'),
    }),
    defects: z
      .object({
        hasDefects: z.boolean(),
        entries: z.array(defectEntrySchema.extend({ description: reqStr })),
      })
      .refine(
        (d) => !d.hasDefects || d.entries.length > 0,
        'Add at least one defect or turn the toggle off'
      ),
    documents: flightLogDocumentsSchema.optional(),
    signOff: signOffSchema,
  })
  // The captain signs twice — once pre-flight, once post-flight.
  // Crew issues are pathed at routeTimes.crew so the drawer routes them to
  // the standalone Crew section (see sectionKeyForIssuePath).
  .superRefine((log, ctx) => {
    if (log.routeTimes.crew.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['routeTimes', 'crew'],
        message: 'Add at least one crew member',
      })
      return
    }
    const pic = log.routeTimes.crew.find((member) => member.duty === 'PIC')
    if (!pic) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['routeTimes', 'crew'],
        message: 'Assign a Pilot Flying before submitting',
      })
      return
    }
    if (!log.signOff.preFlight?.signaturePng) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['signOff'],
        message: 'Pre-flight sign-off required',
      })
    }
    if (!log.signOff.postFlight?.signaturePng) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['signOff'],
        message: 'Post-flight sign-off required',
      })
    }
  })

export type FlightLogCrewEntry = z.infer<typeof flightLogCrewEntrySchema>
export type FlightLogRouteTimes = z.infer<typeof routeTimesSchema>
export type FlightLogConditions = z.infer<typeof conditionsSchema>
export type FlightLogHours = z.infer<typeof hoursSchema>
export type FlightLogFuel = z.infer<typeof fuelSchema>
export type FlightLogDeice = z.infer<typeof deiceSchema>
export type FlightLogOil = z.infer<typeof oilSchema>
export type FlightLogEctm = z.infer<typeof ectmSchema>
export type FlightLogInspection = z.infer<typeof inspectionSchema>
export type FlightLogDefects = z.infer<typeof defectsSchema>
export type FlightLogDocument = z.infer<typeof flightLogDocumentSchema>
export type FlightLogSignOff = z.infer<typeof signOffSchema>
export type FlightLogSignOffPhase = z.infer<typeof signOffPhaseSchema>
export type FlightLogDraft = z.infer<typeof flightLogDraftSchema>
export type FlightLogSubmit = z.infer<typeof flightLogSubmitSchema>

// ---------- lifecycle & review helpers ----------

// Section keys in on-screen order — drives the drawer's scroll targets and
// the "resume at first unfinished section" behavior.
export const flightLogSectionOrder = [
  'crew',
  'routeTimes',
  'hours',
  'apu',
  'fuel',
  'conditions',
  'deice',
  'oil',
  'ectm',
  'inspection',
  'defects',
  'documents',
  'signOff',
] as const

export type FlightLogSectionKey = (typeof flightLogSectionOrder)[number]

// Some sections are views over another section's stored data (crew under
// routeTimes, APU under hours), so issue paths map to a section through
// this helper.
export function sectionKeyForIssuePath(path: readonly PropertyKey[]): string {
  if (path[0] === 'routeTimes' && path[1] === 'crew') return 'crew'
  // APU hours/cycles live under `hours` but render as their own section
  if (path[0] === 'hours' && (path[1] === 'apu' || (path[1] === 'cycles' && path[2] === 'apu')))
    return 'apu'
  return String(path[0] ?? '')
}

export type FlightLogLifecycle = 'notStarted' | 'inProgress' | 'awaitingLanding' | 'complete'

/**
 * Derive the display lifecycle from a log row (or its absence). Presence-based
 * only — never compares clock strings against leg datetimes. Duck-accesses
 * routeTimes because drafts may not pass the full schema.
 */
export function deriveFlightLogLifecycle(
  log: { status: string; routeTimes?: unknown } | null | undefined
): FlightLogLifecycle {
  if (!log) return 'notStarted'
  if (log.status === 'Submitted') return 'complete'
  const rt = log.routeTimes as
    | { offBlock?: string | null; takeOff?: string | null; onBlock?: string | null }
    | null
    | undefined
  if (rt && (rt.offBlock || rt.takeOff) && !rt.onBlock) return 'awaitingLanding'
  return 'inProgress'
}

// Everything the captain's signature certifies — every stored section except
// the sign-off itself.
const signedContentKeys = [
  'natureOfFlight',
  'routeTimes',
  'conditions',
  'hours',
  'fuel',
  'deice',
  'oil',
  'ectm',
  'inspection',
  'defects',
  'documents',
] as const

// Structural JSON equality: object key order is ignored (Postgres jsonb
// reorders keys) and undefined-valued keys count as absent, as in JSON.
function jsonEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false
    return a.every((item, i) => jsonEqual(item, b[i]))
  }
  const aRecord = a as Record<string, unknown>
  const bRecord = b as Record<string, unknown>
  const aKeys = Object.keys(aRecord).filter((key) => aRecord[key] !== undefined)
  const bKeys = Object.keys(bRecord).filter((key) => bRecord[key] !== undefined)
  if (aKeys.length !== bKeys.length) return false
  return aKeys.every((key) => jsonEqual(aRecord[key], bRecord[key]))
}

/**
 * True when a save would change what a signed-off log records. Editing a
 * signed-off log voids its post-flight signature, so the client clears it on
 * the first such change and the API refuses the submit until the captain has
 * signed again. Shared so both sides agree on what counts as an edit.
 */
export function flightLogContentChanged(
  stored: Record<string, unknown>,
  incoming: Record<string, unknown>
): boolean {
  return signedContentKeys.some((key) => {
    // Builds predating the documents field send none; treat as empty
    const blank = key === 'documents' ? [] : null
    return !jsonEqual(stored[key] ?? blank, incoming[key] ?? blank)
  })
}

/**
 * A flight log is filled in after the flight (paper folio → Pronteer), so a
 * leg becomes loggable only once its scheduled departure has passed. Cancelled
 * legs are never loggable. Shared by the API guard and the client gates so
 * both sides agree on the window.
 */
export function isFlightLogOpenForLeg(
  leg: { departureDateTime: Date | string; tripLegStatus?: string | null },
  now: Date = new Date()
): boolean {
  if (leg.tripLegStatus === 'Cancelled') return false
  return new Date(leg.departureDateTime).getTime() <= now.getTime()
}

// Company-wide list bucket (the "Key" on the More → Flight Logs page).
// Derived on the client with the current clock so a leg that departs while
// the page is open flips from upcoming to pending without a refetch.
export type FlightLogPhase = 'complete' | 'pending' | 'upcoming'

export function deriveFlightLogPhase(
  leg: {
    lifecycle: FlightLogLifecycle
    departureDateTime: Date | string
    tripLegStatus?: string | null
  },
  now: Date = new Date()
): FlightLogPhase {
  // A signed-off log is complete regardless of the clock
  if (leg.lifecycle === 'complete') return 'complete'
  if (!isFlightLogOpenForLeg(leg, now)) return 'upcoming'
  return 'pending'
}

// True when the defects section carries at least one Deferred (MEL) entry.
export function hasDeferredDefect(defects: unknown): boolean {
  const parsed = defectsSchema.safeParse(defects)
  if (!parsed.success) return false
  return parsed.data.hasDefects && parsed.data.entries.some((entry) => entry.action === 'D')
}

// First section (in screen order) that fails submit validation, or null when
// the log is submit-ready.
export function getFirstIncompleteSection(sections: unknown): FlightLogSectionKey | null {
  const parsed = flightLogSubmitSchema.safeParse(sections)
  if (parsed.success) return null
  const errorSections = new Set(parsed.error.issues.map((issue) => sectionKeyForIssuePath(issue.path)))
  return flightLogSectionOrder.find((key) => errorSections.has(key)) ?? null
}

// Brought-forward totals sourced from the aircraft's previous submitted log
export type FlightLogBroughtForward = {
  engineCount: number
  airframe: string // h:mm
  engines: string[] // h:mm per engine
  apu: string // h:mm
  cycles: {
    engines: number[]
    apu: number
    landings: number
  }
}

// Identity of the flight whose log supplied the brought-forward totals
export type FlightLogBroughtForwardSource = {
  flightLogId: string
  logNumber: number
  from: string
  to: string
  departureDateTime: Date | string
}

// Per-aircraft totals from submitted logs (getAircraftStats). Hours are the
// sum of each log's logged flight time (take-off → landing), not the
// airframe running total; distance is the sum of the pilot-entered NM.
export type FlightLogAircraftStats = {
  aircraftId: string
  totalFlights: number
  totalFlightMinutes: number
  totalDistanceNm: number
}

// Groups submitted logs by aircraft and sums flight time and distance. A log
// whose routeTimes fail validation still counts as a flight but adds nothing
// to the totals; a missing distance adds 0 (no great-circle fallback).
export function aggregateAircraftStats(
  logs: { aircraftId: string; routeTimes: unknown }[]
): FlightLogAircraftStats[] {
  const byAircraft = new Map<string, FlightLogAircraftStats>()
  for (const log of logs) {
    let row = byAircraft.get(log.aircraftId)
    if (!row) {
      row = {
        aircraftId: log.aircraftId,
        totalFlights: 0,
        totalFlightMinutes: 0,
        totalDistanceNm: 0,
      }
      byAircraft.set(log.aircraftId, row)
    }
    row.totalFlights += 1
    const parsed = routeTimesSchema.safeParse(log.routeTimes)
    if (!parsed.success) continue
    row.totalFlightMinutes += hmmToMinutes(parsed.data.flightTime) ?? 0
    row.totalDistanceNm += parsed.data.distanceNm ?? 0
  }
  return [...byAircraft.values()]
}

// Crew profile stats (crew.getById), bucketed by when the leg departed so the
// profile card can switch between all time / this year / this month without a
// refetch. Bounds are UTC calendar boundaries relative to `now`.
export type FlightStatsPeriod = 'allTime' | 'thisYear' | 'thisMonth'

export type CrewFlightStatTotals = {
  totalFlights: number
  totalFlightMinutes: number
  totalBlockMinutes: number
}

export type CrewFlightStats = Record<FlightStatsPeriod, CrewFlightStatTotals>

// Sums a crew member's submitted flight logs into the three period buckets.
// A leg counts only when its log is Submitted (the log is the proof the flight
// was flown — scheduled times and tripState are ignored) and the leg is not
// Cancelled. A log whose routeTimes fail validation still counts as a flight
// but adds nothing to the time totals.
export function aggregateCrewFlightStats(
  legs: {
    departureDateTime: Date
    tripLegStatus: string
    flightLog: { status: string; routeTimes: unknown } | null
  }[],
  now: Date = new Date()
): CrewFlightStats {
  const empty = (): CrewFlightStatTotals => ({
    totalFlights: 0,
    totalFlightMinutes: 0,
    totalBlockMinutes: 0,
  })
  const stats: CrewFlightStats = { allTime: empty(), thisYear: empty(), thisMonth: empty() }
  const yearStart = Date.UTC(now.getUTCFullYear(), 0, 1)
  const monthStart = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)

  for (const leg of legs) {
    const log = leg.flightLog
    if (leg.tripLegStatus === 'Cancelled' || !log || log.status !== 'Submitted') continue

    const parsed = routeTimesSchema.safeParse(log.routeTimes)
    const flightMinutes = parsed.success ? (hmmToMinutes(parsed.data.flightTime) ?? 0) : 0
    const blockMinutes = parsed.success ? (hmmToMinutes(parsed.data.blockTime) ?? 0) : 0

    const departed = leg.departureDateTime.getTime()
    const buckets: CrewFlightStatTotals[] = [stats.allTime]
    if (departed >= yearStart) buckets.push(stats.thisYear)
    if (departed >= monthStart) buckets.push(stats.thisMonth)
    for (const bucket of buckets) {
      bucket.totalFlights += 1
      bucket.totalFlightMinutes += flightMinutes
      bucket.totalBlockMinutes += blockMinutes
    }
  }
  return stats
}
